"use client"

import { useEffect } from "react"

/** Ngưỡng bề rộng render tối thiểu (px) để coi là ảnh nội dung đáng phóng to,
 *  bỏ qua icon/emoji nhỏ. */
const MIN_IMAGE_WIDTH = 100

/** Khoảng vuốt ngang tối thiểu (px) để tính là chuyển ảnh trên mobile. */
const SWIPE_THRESHOLD = 50

type LightboxItem = { src: string; alt: string }

const NAV_LABELS: Record<string, { prev: string; next: string; close: string }> = {
  vi: { prev: "Ảnh trước", next: "Ảnh kế tiếp", close: "Đóng" },
  en: { prev: "Previous image", next: "Next image", close: "Close" },
  cs: { prev: "Předchozí obrázek", next: "Další obrázek", close: "Zavřít" },
}

/** Ảnh bản gốc (album gắn data-full-src), fallback bản đang hiển thị. */
const fullSrcOf = (img: HTMLImageElement) => img.getAttribute("data-full-src") || img.currentSrc || img.src

/**
 * Lightbox cho ảnh trong nội dung bài viết. Hai vai trò:
 *
 * 1) Định nghĩa hàm toàn cục `window.openAlbumLightbox` để các album cũ
 *    (inline `onclick="openAlbumLightbox(...)"`) luôn mở được — kể cả khi
 *    điều hướng mềm SPA, lúc đó <script> inline của album không tự chạy.
 *
 * 2) Delegated click: cho phép phóng to MỌI ảnh trong `.content-wrapper`,
 *    không chỉ ảnh album — UX nhất quán cho site nội dung. Có rào chắn:
 *    chỉ trong thân bài, bỏ ảnh là link, bỏ ảnh quá nhỏ. Ảnh thuộc album
 *    (`.ck-image-album`) mở ở chế độ album: có nút ‹ › / phím ← → / vuốt để
 *    duyệt cả album mà không phải đóng rồi bấm từng ảnh.
 *
 * Render null — chỉ để gắn/cởi listener và định nghĩa hàm.
 */
export default function ContentLightbox() {
  useEffect(() => {
    if (typeof window === "undefined") return

    const open = (items: LightboxItem[], startIndex = 0) => {
      // Tránh mở chồng nhiều lightbox
      if (document.querySelector(".album-lightbox")) return
      if (!items.length) return

      const labels = NAV_LABELS[document.documentElement.lang?.slice(0, 2)] ?? NAV_LABELS.vi
      const isAlbum = items.length > 1
      let index = Math.min(Math.max(startIndex, 0), items.length - 1)

      const lb = document.createElement("div")
      lb.className = "album-lightbox"
      lb.setAttribute("role", "dialog")
      lb.setAttribute("aria-modal", "true")

      const close = document.createElement("button")
      close.type = "button"
      close.className = "album-lightbox-close"
      close.setAttribute("aria-label", labels.close)
      close.innerHTML = "&times;"

      const img = document.createElement("img")
      lb.appendChild(close)
      lb.appendChild(img)

      let counter: HTMLDivElement | null = null
      if (isAlbum) {
        const makeNav = (dir: "prev" | "next") => {
          const btn = document.createElement("button")
          btn.type = "button"
          btn.className = `album-lightbox-nav album-lightbox-${dir}`
          btn.setAttribute("aria-label", dir === "prev" ? labels.prev : labels.next)
          btn.innerHTML = dir === "prev" ? "&#10094;" : "&#10095;"
          btn.addEventListener("click", e => {
            e.stopPropagation()
            go(dir === "prev" ? -1 : 1)
          })
          return btn
        }
        lb.appendChild(makeNav("prev"))
        lb.appendChild(makeNav("next"))

        counter = document.createElement("div")
        counter.className = "album-lightbox-counter"
        lb.appendChild(counter)
      }

      const render = () => {
        const item = items[index]
        // Dùng property (không phải innerHTML) nên không có nguy cơ HTML injection
        img.src = item.src
        img.alt = item.alt ?? ""
        if (counter) counter.textContent = `${index + 1} / ${items.length}`
        // Tải trước ảnh kề bên để bấm chuyển không bị chờ
        if (isAlbum) {
          ;[-1, 1].forEach(d => {
            const pre = new Image()
            pre.src = items[(index + d + items.length) % items.length].src
          })
        }
      }

      // Vòng lặp: ảnh cuối → ảnh đầu, giống slider album
      const go = (delta: number) => {
        index = (index + delta + items.length) % items.length
        render()
      }

      const remove = () => {
        lb.remove()
        document.removeEventListener("keydown", onKey)
      }
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") remove()
        else if (isAlbum && e.key === "ArrowLeft") go(-1)
        else if (isAlbum && e.key === "ArrowRight") go(1)
      }

      lb.addEventListener("click", e => {
        if (e.target === lb || e.target === close) remove()
      })

      if (isAlbum) {
        let touchX: number | null = null
        lb.addEventListener("touchstart", e => {
          touchX = e.touches[0]?.clientX ?? null
        }, { passive: true })
        lb.addEventListener("touchend", e => {
          if (touchX === null) return
          const dx = (e.changedTouches[0]?.clientX ?? touchX) - touchX
          touchX = null
          if (Math.abs(dx) >= SWIPE_THRESHOLD) go(dx > 0 ? -1 : 1)
        })
      }

      document.addEventListener("keydown", onKey)
      render()
      document.body.appendChild(lb)
    }

    // API cũ cho album lưu sẵn onclick="openAlbumLightbox(src, alt)" — mở 1 ảnh.
    // Thường sẽ no-op vì delegated handler (capture phase) đã mở dạng album trước.
    ;(window as unknown as { openAlbumLightbox?: (src: string, alt: string) => void }).openAlbumLightbox = (
      src,
      alt,
    ) => open([{ src, alt: alt ?? "" }])

    // Delegated: phóng to mọi ảnh trong thân bài. Dùng capture phase để chạy
    // TRƯỚC onclick inline của album cũ → luôn mở được chế độ album; lời gọi
    // openAlbumLightbox sau đó no-op nhờ guard chống mở chồng trong open().
    const onContentImageClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!(target instanceof HTMLImageElement)) return
      if (!target.closest(".content-wrapper")) return // chỉ trong thân bài
      if (target.closest("a")) return // ảnh là link → để điều hướng
      if (target.clientWidth && target.clientWidth < MIN_IMAGE_WIDTH) return // bỏ icon nhỏ

      const album = target.closest(".ck-image-album")
      const albumImgs = album
        ? Array.from(album.querySelectorAll<HTMLImageElement>("img.album-img"))
        : []

      if (albumImgs.length > 1 && albumImgs.includes(target)) {
        e.preventDefault()
        open(
          albumImgs.map(el => ({ src: fullSrcOf(el), alt: el.alt || "" })),
          albumImgs.indexOf(target),
        )
        return
      }

      const full = fullSrcOf(target)
      if (!full) return

      e.preventDefault()
      open([{ src: full, alt: target.alt || "" }])
    }
    document.addEventListener("click", onContentImageClick, true)

    return () => {
      document.removeEventListener("click", onContentImageClick, true)
      // Không xoá hàm openAlbumLightbox khi unmount để album trang khác vẫn dùng;
      // chỉ dọn lightbox còn sót (nếu có).
      document.querySelector(".album-lightbox")?.remove()
    }
  }, [])

  return null
}
